import { useSelector } from "react-redux";
import { getCustomMetadataLinkOptions } from "redux/SingleDataset/selectors";
import { CustomMetadata, CustomMetadataLinkOperators } from "types";
import { useCustomMetadataModal } from "hooks/useCustomMetadataModal";
import { useModalOperatorsAndValues } from "hooks/CustomMetadata/useModalOperatorsAndValues";

import BaseQueryModal from "./BaseQueryModal";
import { CustomLinkIcon } from "assets/icons/CustomMetadataIcons";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import {useTextOrMultiQueryModal} from "hooks/UseQueryModal/useTextOrMultiQueryModal";

interface LinkModalProps {
  field: CustomMetadata;
  editQueryId?: string;
}

export default function LinkModal({ editQueryId, field }: LinkModalProps) {
  const { modalRef, handleSubmission, fieldName } = useCustomMetadataModal({
    field,
    editQueryId,
  });
  const linkOptions = useSelector(getCustomMetadataLinkOptions);

  const {
    operator,
    setOperator,
    operatorOptions,
    values,
    setValues,
    selectedIds,
    searchText,
    setSearchText,
    mode,
    previousValue,
    previousOperator,
  } = useModalOperatorsAndValues({
    type: "link",
    field,
    editQueryId,
    options: linkOptions,
  });

  const selectedValueIds = values
    .filter((v: any) => v.selected)
    .map((v: any) => v.id);

  const { isSubmitDisabled } = useTextOrMultiQueryModal({
    mode,
    selectedItems: values.filter((v: any) => v.selected).map(v => v.text),  // string[]
    previousItems: previousValue || [],
    searchText,
    previousText: previousValue && !Array.isArray(previousValue) ? previousValue : "",
    selectedOperator: operator,
    previousOperator,
    editQueryId,
    existingQueryId: editQueryId || null,
  });



  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    let value: string | string[];
    if (selectedValueIds.length > 0) {
      value = values.filter((v: any) => v.selected).map((v: any) => v.text);
    } else {
      value = searchText.trim();
    }
    handleSubmission(operator, value);
  };

  const operators = {
    options: operatorOptions,
    selected: operator,
    onSelect: setOperator,
  };

  const handleRemoveValue = (id: number | string) => {
    setValues(
      values.map((v: any) => (v.id === id ? { ...v, selected: false } : v))
    );
  };

  const handleSelectValue = (id: number | string) => {
    setValues(
      values.map((v: any) =>
        v.id === id ? { ...v, selected: !v.selected } : v
      )
    );
  };

  const inputComponent = {
    text: (
      <QueryModalInput searchText={searchText} onInputChange={setSearchText} />
    ),
    multiple: (
      <QueryModalInput
        mode="multiple"
        options={values}
        searchText={searchText}
        selected={selectedIds}
        onRemove={handleRemoveValue}
        onSelect={handleSelectValue}
        onInputChange={setSearchText}
      />
    ),
  };

  return (
    <BaseQueryModal<CustomMetadataLinkOperators>
      title={fieldName}
      keepOpenOnLoading
      modalRef={modalRef}
      icon={<CustomLinkIcon />}
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={handleSubmit}
      operators={operators}
    >
      {inputComponent[mode]}
    </BaseQueryModal>
  );
}
